import { useEffect, useState } from 'react'
import { INVITE_DAYS, type Group, type InviteCreated } from '@sp/shared'
import { useSignedIn } from '../session.tsx'
import { api } from '../lib/api.ts'
import { HttpError } from '../lib/http.ts'
import { errorMessage } from '../lib/useResource.ts'
import { Sheet } from './Sheet.tsx'
import { Icon } from './Icon.tsx'
import { Button, Paragraph, TextField } from './ui.tsx'

/**
 * Pozvanka = jednorazovy odkaz, ktery se posle treba v Messengeru.
 * Odkaz vznika az na tlacitko, ne pri otevreni panelu — jinak by kazde
 * nahodne otevreni nechalo ve skupine visici pozvanku.
 */
export function InviteSheet({ open, onClose, group }: { open: boolean; onClose(): void; group: Group }) {
  const s = useSignedIn()
  const [label, setLabel] = useState('')
  const [created, setCreated] = useState<InviteCreated | null>(null)
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) return
    setLabel('')
    setCreated(null)
    setCopied(false)
    setError(null)
  }, [open])

  const url = created ? `${window.location.origin}/p/${created.token}` : ''
  const canShare = typeof navigator.share === 'function'

  async function create() {
    setBusy(true)
    setError(null)
    try {
      setCreated(await api.invites.create(group.id, label.trim() || null))
    } catch (err) {
      setError(err instanceof HttpError && err.isOffline ? 'Pozvánku jde vytvořit jen se signálem.' : errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  async function share() {
    try {
      await navigator.share({
        title: 'Pozvánka do skupiny',
        text: `${s.user.name} tě zve do skupiny „${group.name}“ v Sériových nákupech.`,
        url,
      })
    } catch (err) {
      // Zavreni systemoveho panelu sdileni neni chyba.
      if (!(err instanceof DOMException && err.name === 'AbortError')) setError('Sdílení se nepovedlo, zkopíruj odkaz.')
    }
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
    } catch {
      setError('Odkaz se nepodařilo zkopírovat. Označ ho a zkopíruj ručně.')
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title="Pozvat do skupiny" subtitle={group.name}>
      <div className="stack">
        <Paragraph tone="fg">
          Pošli odkaz, komu chceš – třeba v Messengeru. Kdo ho otevře, přihlásí se nebo založí účet a přidá se do skupiny.
        </Paragraph>
        <TextField
          label="Pro koho"
          placeholder="Nepovinné – ať pozvánku v seznamu poznáš"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={60}
          disabled={created !== null}
        />
        {created ? (
          <>
            <label className="link-box">
              <Icon name="link" size={20} />
              <input readOnly value={url} aria-label="Odkaz s pozvánkou" onFocus={(e) => e.currentTarget.select()} />
            </label>
            {canShare && (
              <Button kind="primary" icon="share" full onClick={() => void share()}>
                Sdílet odkaz
              </Button>
            )}
            <Button kind={canShare ? 'outlined' : 'primary'} icon={copied ? 'check' : 'copy'} full onClick={() => void copy()}>
              {copied ? 'Zkopírováno' : 'Kopírovat odkaz'}
            </Button>
          </>
        ) : (
          <Button kind="primary" icon="link" full disabled={busy} onClick={() => void create()}>
            Vytvořit odkaz
          </Button>
        )}
        {error && <Paragraph tone="error">{error}</Paragraph>}
        <Paragraph size="meta">
          Odkaz platí {INVITE_DAYS} dní a lze ho použít jen jednou. Dokud ho nikdo nepoužije, najdeš ho ve skupině v části
          Čeká na přijetí a můžeš ho zrušit.
        </Paragraph>
      </div>
    </Sheet>
  )
}
